import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {forkJoin, Observable} from 'rxjs';
import {SettingsService} from '../../services/settings.service';
import {UserstoryService} from '../../services/userstory.service';
import {BoardState} from '../../models/boardstate';
import {CommunicationService, Listener} from '../../services/communication.service';
import {AwallComponentEvent} from '../../models/awallComponentEvent';
import {NotifierService} from 'angular-notifier';
import {KanbanProjectService} from '../../services/kanban-project.service';
import {Column} from '../../models/column';
import {KanbanGridNoswimlaneComponent} from '../../components/kanban/grids/kanban-grid-noswimlane/kanban-grid-noswimlane.component';
import {KanbanBoardDirective} from './kanban-board.directive';
import {IKanbanGrid} from '../../components/kanban/grids/iKanbanGrid';
import {Issue} from '../../models/issue';
import {TaskcardDetailComponent} from '../../components/kanban/taskcard-detail/taskcard-detail.component';
import {HTMLElementPositioningHelper} from '../../util/htmlelement-positioning-helper';
import {SynchronizationService} from '../../services/synchronization.service';
import {DynamicElementService} from '../../services/dynamicElement.service';
import {Task} from '../../models/task';
import {InteractionService} from '../../services/interaction.service';
import {Userstory} from '../../models/userstory';
import {Status} from '../../models/status';
import {TaskboardSynchroHelper} from '../taskboard-view/taskboard-synchro-helper';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {Image} from '../../models/image';
import {HighlightTeammemberService} from '../../services/highlight-teammember.service';
import {Sticker} from '../../models/sticker';
import {StickerDetailComponent} from '../../components/sticker-detail/sticker-detail.component';
import {StickerService} from '../../services/sticker.service';
import {KanbanGridDefaultComponent} from '../../components/kanban/grids/kanban-grid-default/kanban-grid-default.component';
import {IssuecardHelper} from '../../components/issuecard/issuecard.helper';
import {EventIssuecardOpened} from '../../models/syncEvents/realtimeEvents/eventIssuecardOpened';
import {EventIssuecardUpdatedExt} from '../../models/syncEvents/realtimeEvents/eventIssuecardUpdatedExt';
import {ComponentEvent} from '../../models/enums/componentEvent';
import {BoardData} from '../../models/boardData';
import {BaseBoardComponent} from '../baseBoard';
import {OnlineuserService} from '../../services/onlineuser.service';

@Component({
  selector: 'app-kanban-board-view',
  templateUrl: './kanban-board-view.component.html',
  styleUrls: ['./kanban-board-view.component.scss']
  })
export class KanbanBoardViewComponent extends BaseBoardComponent implements OnInit, OnDestroy, Listener<AwallComponentEvent> {
  public boardConfiguration: BoardState;
  public columnStatuses: Status[] = [];
  public taskboardData = [];
  public selectedSwimlane: String = 'default';
  public expediteLane = (/true/i).test(localStorage.getItem('expediteLane'));
  // This array keeps track of the tasks that have been dropped outside a cdkDropList
  public floatingTasks: Task[] = [];
  // This array keeps track of the dynamically created issuecards. Each issuecard corresponds to one floating task
  public openCards: (TaskcardDetailComponent | StickerDetailComponent)[] = [];
  @ViewChild(KanbanBoardDirective) appKanbanGrid: KanbanBoardDirective;
  private dynamicElementInsertionPoint: ViewContainerRef;
  private releaseCoord: ClientRect;
  private child;
  public static readonly componentId = 'KanbanBoardViewComponent';

  constructor(
    private userStoryService: UserstoryService,
    private projectService: KanbanProjectService,
    private settingsService: SettingsService,
    private communicationService: CommunicationService,
    private interactionService: InteractionService,
    private notifier: NotifierService,
    private changeDetection: ChangeDetectorRef,
    private syncHelper: TaskboardSynchroHelper,
    private issueCardHelper: IssuecardHelper,
    private positionHelper: HTMLElementPositioningHelper,
    private syncService: SynchronizationService,
    private dynamicElementService: DynamicElementService,
    private highlightService: HighlightTeammemberService,
    private stickerService: StickerService,
    private onlineUserService: OnlineuserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.communicationService.registerComponent(KanbanBoardViewComponent.componentId, this);
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(() => {
      if (this.settingsService.getProjectType() === 'scrum') {
        this.notifier.notify('error', 'Scrum Project can not be loaded in Kanban-Board...');
        return;
      } else {
        this.notifier.notify('info', 'Loading Data...');
        this.loadViewData();
      }
    }));

    this.dynamicElementInsertionPoint = this.dynamicElementService.getInsertionPoint();
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(KanbanBoardViewComponent.componentId);
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    super.onCommunicationEvent(event);

    switch (event.name) {
      case ComponentEvent.WIDGET_TEAM_HIGHLIGHT_CHANGED: {
        this.updateHighLightColors();
        break;
      }
      // Add dropped image as attachement to the corresponding issue
      case 'image-dropped': {
        const issueId = event.value['issueId'];
        const image: Image = event.value['image'];
        this.userStoryService.addAttachmentToIssue(this.settingsService.getProjectName(), issueId, image).subscribe(
          _ => {
            // if this task is shown in an open issuecard we make the card update the task to show the new image-attachment
            const card: TaskcardDetailComponent = this.getIssueCard(issueId);
            if (card) {
              card.updateExtendedData();
              this.syncService.sendRealTimeEventNew(new EventIssuecardUpdatedExt(issueId, this.onlineUserService.getAvatarUrl()));
            }
          });
        break;
      }
      case 'sticker-dropped': {
        const issueId = event.value['issueId'];
        const stickerId = event.value['stickerId'];
        const task: Task = this.getTask(issueId);
        const userstory: Userstory = this.getUserstory(issueId);
        const sticker: Sticker = new Sticker(stickerId, issueId);
        const issue = (task) ? task : userstory;

        if (issue) {
          this.stickerService.addSticker(sticker).subscribe(
            data => issue.stickers.push(this.stickerService.mapJSONToSticker(data))
          );
        } else {
          console.error('Sticker-Dropped-Event: Issue not found with id: ', issueId);
        }

        this.updateHighLightColors();
        break;
      }
    }
  }

  expediteLaneChange(state): void {
    this.expediteLane = state;
    this.child.instance.data.expediteLane = state;
    localStorage.setItem('expediteLane', state);
    this.prepareDataForBoard();
  }

  public updateHighLightColors() {
    this.taskboardData.forEach(userstory => {
      userstory.highlightColor = this.highlightService.getHighlightColorName(userstory.assignee);

      userstory.tasks.forEach(task => {
        task.highlightColor = this.highlightService.getHighlightColorName(task.assignee);
      });
    });
  }

  /**
   * Returns the userstory with <id> from the taskboardata or null if not found.
   * Assumes there is <= 1 task with <id>.
   * @param id userstoryId
   */
  getUserstory(id: number): Userstory {
    let result: Userstory = null;

    this.taskboardData.forEach(userstory => {
      if (userstory.id === id) {
        result = userstory;
      }
    });
    return result;
  }

  loadComponent() {
    let component;
    switch (this.selectedSwimlane) {
      case 'default':
        component = KanbanGridDefaultComponent;
        break;
      case 'noswimlane':
        component = KanbanGridNoswimlaneComponent;
        break;
      default:
        component = KanbanGridDefaultComponent;
    }

    const viewContainerRef = this.appKanbanGrid.viewContainerRef;
    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(component);
    this.child = componentRef;

    (<IKanbanGrid>componentRef.instance).data = {
      taskboardData: this.taskboardData,
      boardConfiguration: this.boardConfiguration,
      expediteLane: this.expediteLane,
      parent: this
    };
  }

  /**
   * this requests the userstory and boardstate data and starts to sort it when both are downloaded
   */
  loadViewData() {
    this.boardConfiguration = null;
    this.taskboardData = [];

    const projectName = this.settingsService.getProjectName();

    forkJoin([
      this.userStoryService.getIssuesFromProject(projectName, true),
      this.projectService.getBoardStates(projectName)
    ]).subscribe(([userStoriesData, boardConfiguration]) => {
      this.boardConfiguration = boardConfiguration;
      const columns: Column[] = [];
      this.boardConfiguration.columns.forEach(column => {
        if (column.name === 'Backlog') { return; }
        columns.push(column);
      });
      this.boardConfiguration.columns = columns;
      this.taskboardData = userStoriesData;

      this.loadComponent();
    });
  }

  replaceDropListElementWithIssueCard(task: Task, taskContainerArray, position, isRelativePosition: boolean) {
    const card: TaskcardDetailComponent = this.createIssueCard(task, taskContainerArray);
    const cardElement = card.getElementRef().nativeElement;
    cardElement.cardObject = card;

    // if we are reacting to a sync-call we need to position our new Issuecard at the same relative position of the remote client
    if (isRelativePosition) {
      this.positionHelper.setRelativePosition(cardElement, position.left, position.top);
    } else {
      this.positionHelper.setAbsolutePosition(cardElement, position.left, position.top);
    }

    // Move subtask from taskboardAppData-array to floatingTasks-array
    const taskIndex = taskContainerArray.indexOf(task);
    this.floatingTasks.push(taskContainerArray.splice(taskIndex, 1)[0]);
  }

  /**
   * Save the coordinates where the issuecard was released (used to create floating card at the right coordinates)
   */
  onCardReleased(event) {
    const coord: ClientRect = document.getElementsByClassName('cdk-drag-preview')[0].getBoundingClientRect();
    this.releaseCoord = coord;
  }

  /**
   * This fires when a closed issuecard (more precicely a list element of a cdkDroplist) is moved.
   * The cdkDragDrop will show a Drag-Preview element during drag.
   */
  onCardMoved(event) {
    const taskId = event.source.element.nativeElement.getAttribute('data-taskcardid');
    const data = {
      'taskId': taskId,
      'position': this.positionHelper.getRelativePosition(event.pointerPosition.x, event.pointerPosition.y)
    };

    this.syncService.sendRealTimeEvent('TaskcardDragged', data);
  }

  onIssueDropped(event: CdkDragDrop<string[]>, expedited: boolean) {
    const task = <Task>event.item.data;
    this.syncService.sendRealTimeEvent('TaskcardDragStopped', { 'taskId': task.id });

    // Issue was dropped outside a droplist. We hide the list-item and show the detailed issuecard
    if (!event.isPointerOverContainer && event.item) {
      // There could already be an issue-card from a doubleclick, if so close it (not a very clean solution)
      const taskContainerArray = event.item.dropContainer.data['tasks'];
      const existingCard = this.getIssueCard(task.id);
      if (existingCard) {
        this.dynamicallyRemoveIssueCardComponent(existingCard);
      }

      const positionAbsolute = { 'left': this.releaseCoord.left, 'top': this.releaseCoord.top };
      this.replaceDropListElementWithIssueCard(task, taskContainerArray, positionAbsolute, false);
    }

    // Issue was dropped on another droplist. We move it to the new droplist and change the status accordingly
    if (event.isPointerOverContainer && (event.previousContainer !== event.container)) {
      // Update the statusId on the subtask and persist this change
      const issueCurrentlyInArray = event.previousContainer.data['tasks'];
      // const issue = issueCurrentlyInArray[event.previousIndex];
      const issue = event.item.data;
      const newStatusId: number = +(event.container.element.nativeElement.getAttribute('data-columnid'));
      const newStatus = this.boardConfiguration.getStatus(newStatusId);

      if (issue.status.name === 'Backlog') {
        issue['tasks'].forEach(subtask => {
          subtask.status = newStatus;
          this.userStoryService.updateTask(this.settingsService.getProjectName(), subtask);
        });
        this.taskboardData.unshift(issue);
      }

      issue.status = newStatus;
      issue.isExpedited = expedited;

      if (issue.isSubissue) {
        this.userStoryService.updateTask(this.settingsService.getProjectName(), issue);
      } else {
        this.userStoryService.updateIssue(this.settingsService.getProjectName(), issue.id, issue);
      }
      // Move Subtask to new droplist
      this.moveIssueToNewDroplist(event);
      this.prepareDataForBoard();
    }
  }

  moveIssueToNewDroplist(event: CdkDragDrop<string[]>) {
    const issueCurrentlyInArray = event.previousContainer.data['tasks'];
    const issueDroppedIntoArray = event.container.data['tasks'];
    // const issue = issueCurrentlyInArray[event.previousIndex];
    const issue = event.item.data;

    // Add subtask to the target array
    issueDroppedIntoArray.push(issue);

    for (let i = 0; i < issueCurrentlyInArray.length; i++) {
      if (issueCurrentlyInArray[i].id === issue.id) {
        issueCurrentlyInArray.splice(i, 1);
      }
    }
  }

  /**
   * A minified issue-card has been double clicked. Open the detailed Issuecard (if it doesnt exist already) without
   * changing the position/view of the minfied card.
   */
  onIssueDoubleClicked(event: MouseEvent, issue: Issue) {
    if (this.getIssueCard(issue.id)) {
      return;
    }
    // Create a new card and open it at the correct position
    const card: TaskcardDetailComponent = this.createIssueCard(issue, null);
    this.positionHelper.setAbsolutePosition(card.getElementRef().nativeElement, event.clientX, event.clientY);

    // Send a sync message to other client telling them we opened the card on this position
    const position = this.positionHelper.getRelativePositionOfElement(card.getElementRef().nativeElement);
    const realtimeEvent = new EventIssuecardOpened(card.issue.id, null, card.issue.isSubissue, position,
      this.onlineUserService.getAvatarUrl());
    this.syncService.sendRealTimeEventNew(realtimeEvent);
  }

  /**
   * @param issue the issue to be displayed in the issuecard
   * @param taskContainer droplist-container of the corresponding task. use 'null' if the task hasnt been moved out of the droplist.
   */
  createIssueCard(issue: Issue, taskContainer: Array<any>): TaskcardDetailComponent {
    const componentRef: ComponentRef<TaskcardDetailComponent> = this.dynamicElementInsertionPoint.createComponent(TaskcardDetailComponent);
    const card: TaskcardDetailComponent = componentRef.instance;

    // Set the task and taskContainer on the card for easy access once the card is destroyed and the task has to be moved
    // back to its container
    card.issue = issue;
    card.issueContainer = taskContainer;
    card.issueIsFloating = (taskContainer != null);
    card.kanbanBoardView = this;
    card.getElementRef().nativeElement.setAttribute('data-issueid', issue.id);
    this.openCards.push(card);
    this.issueCardHelper.setupIssueCardAsDraggable(card);

    return card;
  }

  /**
   * Returns the first task with <id> from the floatingtask-array or undefined if not found.
   * @param id taskId
   * @param remove task is removed from its floatingtask-array if true
   */
  getFloatingTask(id: number, remove?: boolean): Task {
    const result: Task = this.floatingTasks.find(task => Number(task.id) === Number(id));
    if (result && remove) {
      const index: number = this.floatingTasks.indexOf(result);
      if (index !== -1) {
        this.floatingTasks.splice(index, 1);
      }
    }
    return result;
  }

  /**
   * Returns the first task with <id> from the taskboardata or null if not found.
   * @param id taskId
   * @param remove task is removed from its taskboardata-array if true
   */
  private getTaskboardTask(id: number, remove?: boolean): Task {
    let result: Task = null;

    this.taskboardData.forEach(entry => {
      entry.tasks.forEach(task => {
        if (Number(task.id) === Number(id)) {
          result = task;
          if (remove) {
            const index: number = entry.tasks.indexOf(result);
            if (index !== -1) {
              entry.tasks.splice(index, 1);
            }
          }
        }
      });
    });

    this.prepareDataForBoard();

    return result;
  }

  /**
   * Returns the first task with <id> from the taskboardata or floatingtask or null if not found.
   * @param id taskId
   * @param remove task is removed from its array if true
   */
  getTask(id: number, remove?: boolean): Task {
    let result: Task = this.getTaskboardTask(id, remove);

    if (!result) {
      result = this.getFloatingTask(id, remove);
    }
    if (!result) {
      console.warn('getTask() couldnt find task with id: ', id);
    }
    return result;
  }

  /**
   * Puts the task with <id> in the correct array of taskboardData according to its status. Used
   * after changing the the status of a task programmatically (as opposed to dragging it in GUI).
   */
  public placeTask(id: number) {
    // Find the Task and remove it from its current array
    const task: Task = this.getTask(id, false);

    // Place the task in the correct array in taskboardData according to its status
    if (task) {
      this.prepareDataForBoard();
    }
  }

  public prepareDataForBoard() {
    this.child.instance.prepareDataForBoard(this.taskboardData);
  }

  /**
   * Adds a subtask to the correct array in the TaskboardData-Array.
   * This method assumes that the array for the parent-Userstory of the subtask exists!
   */
  addSubtaskToTaskboardDataModel(subtask: Task) {
    let success = false;
    this.taskboardData.forEach(userstory => {
      if (userstory.id === subtask.parentIssueId) {
        userstory.tasks.push(subtask);
        success = true;
      }
    });
    if (!success) {
      console.error('Subtask couldnt be placed in taskboard-data!');
    } else {
      this.prepareDataForBoard();
    }
  }

  dynamicallyRemoveIssueCardComponent(card: TaskcardDetailComponent) {
    const cardIndex = this.openCards.indexOf(card);

    /* Move the task back to its droplist-array if was floating*/
    if (card.issueIsFloating && (card.issue instanceof Task || card.issue instanceof Userstory)) {
      const task: Task = <Task>card.issue;
      this.placeTask(task.id);
      // Check for newly empty/non-empty columns as we moved tasks back to the taskboardData
    }
    // Remove the card from issueCards-array
    this.openCards.splice(cardIndex, 1);
    this.issueCardHelper.removeCardFromGlobalReferences(card);
  }

  /**
   * Create new DoD Popup
   * @param issue for which the popup is created
   */
  onIssueSetToDone(issue: Issue) {
    // this.definitionOfDoneService.emitNewDoDPopup(issue);
  }

  /**
   * Returns the issueCard with the given <issueId> or undefined if no matching card is found.
   */
  getIssueCard(issueId: number): TaskcardDetailComponent {
    return <TaskcardDetailComponent>this.openCards.find(card => Number(card.issue.id) === Number(issueId));
  }



  public removeUserstoryFromDataModel(id: number) {
    let userstoryIndex = -1;
    this.taskboardData.forEach((entry, index) => {
      if (Number(entry.id) === Number(id)) {
        userstoryIndex = index;
      }
    });
    this.taskboardData.splice(userstoryIndex, 1);

    this.prepareDataForBoard();
  }

  dynamicallyRemoveStickerCardComponent(card: StickerDetailComponent) {
    const cardIndex = this.openCards.indexOf(card);
    // Remove the card from issueCards-array
    this.openCards.splice(cardIndex, 1);
    // Remove the card from the dynamic container
    this.dynamicElementInsertionPoint.remove(cardIndex);
  }

  dynamicallyAddStickerCardComponent(task: Issue, sticker: Sticker, stickerContainer: Array<any>): StickerDetailComponent {
    const componentRef: ComponentRef<StickerDetailComponent> = this.dynamicElementInsertionPoint.createComponent(StickerDetailComponent);
    const card: StickerDetailComponent = componentRef.instance;

    // Set the task and taskContainer on the card for easy access once the card is destroyed and the task has to be moved
    // back to its container
    this.openCards.push(card);
    card.issue = task;
    card.sticker = sticker;
    card.issueContainer = stickerContainer;
    card.issueIsFloating = (stickerContainer != null);
    card.aWallViewKanban = this;

    return card;
  }

  getBoardData(): Observable<BoardData> {
    throw new Error('Method not implemented.');
  }

}
